/**
 * PHYSICAL-RISK-MOVE-TO-RPS-MODAL
 * Deplace les risques selectionnes vers les RPS : un axe par risque, puis un recap par fonction. Le niveau
 * global d'une fonction n'est pas recalcule : il s'ajuste a la main depuis le recap.
 */
import ListDropdown from "@/components/list-dropdown"
import ListItem from "@/components/list-item"
import Modal from "@/components/modal"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import establishmentStore from "@/entities/establishment/model/establishment.store"
import populationStore from "@/entities/population/model/population.store"
import { Population } from "@/entities/population/model/population.types"
import { getRpsRisk, RPS_RISK_AXES, RpsRiskAxis } from "@/entities/population/model/rps-risk"
import RpsRiskCell, { RPS_CELL_WIDTH, RPS_GLOBAL_CELL_WIDTH } from "@/entities/population/ui/RpsRiskCell"
import RpsEvaluationEditModal from "@/entities/population/ui/modal/RpsEvaluationEditModal"
import { STATUS_SAVING } from "@/redux/_status.types"
import { useState } from "react"
import { toast } from "react-toastify"
import physicalRiskStore from "../../model/physical-risk.store"

interface Props {
  riskIds: string[]
  onClose: () => void
  onMoved: (riskIds: string[]) => void
}

const AXES = RPS_RISK_AXES.map((axis) => ({ id: axis.id, name: axis.label }))

export default function PhysicalRiskMoveToRpsModal({ riskIds, onClose, onMoved }: Props) {
  const dispatch = useAppDispatch()
  const allRisks = useAppSelector(physicalRiskStore.selectors.list)
  //Les risques deplaces sortent de la liste : on garde ceux du depart
  const [risks] = useState(() => allRisks.filter((risk) => riskIds.includes(risk.id)))
  const populations: Population[] = useAppSelector(populationStore.selectors.list)
  const [axes, setAxes] = useState<Record<string, RpsRiskAxis>>({})
  const [isMoving, setIsMoving] = useState(false)
  //Fonctions touchees par le deplacement : leur presence ouvre le recap
  const [movedPopulationIds, setMovedPopulationIds] = useState<string[] | null>(null)
  const [editingPopulation, setEditingPopulation] = useState<Population | null>(null)

  //Une situation RPS se rattache a une fonction : un risque sans fonction ne peut pas etre deplace
  const movableRisks = risks.filter((risk) => risk.populationIds.length)

  async function move() {
    setIsMoving(true)
    const movedIds: string[] = []
    const populationIds = new Set<string>()
    for (const risk of movableRisks) {
      const response: any = await dispatch(
        establishmentStore.thunks.moveRiskToRps({ riskId: risk.id, axis: axes[risk.id] ?? RPS_RISK_AXES[0].id })
      )
      if (response.payload?.error) {
        toast.error(`${risk.cName} : ${response.payload.error}`)
        continue
      }
      movedIds.push(risk.id)
      risk.populationIds.forEach((id) => populationIds.add(id))
    }
    setIsMoving(false)
    onMoved(movedIds)
    setMovedPopulationIds([...populationIds])
  }

  if (movedPopulationIds) {
    return (
      <>
        {editingPopulation && (
          <RpsEvaluationEditModal population={editingPopulation} onClose={() => setEditingPopulation(null)} />
        )}
        <Modal title="Risques psychosociaux des fonctions" isLarge disableClickOutside onNext={onClose} onNextCta="Terminer">
          <div className="mb-20 grey-t">Cliquez sur le niveau d’une fonction pour l’ajuster.</div>
          {populations
            .filter((population) => movedPopulationIds.includes(population.id))
            .map((population) => (
              <div key={population.id} className="flex items-center gap-[8px] py-[6px]">
                <div className="min-w-0 flex-1 truncate">{population.name}</div>
                <div className="shrink-0" style={{ width: RPS_GLOBAL_CELL_WIDTH }}>
                  <RpsRiskCell
                    level={population.cRpsLevel}
                    isGlobal
                    label="Niveau de risque"
                    onClick={() => setEditingPopulation(population)}
                  />
                </div>
                {RPS_RISK_AXES.map((axis) => (
                  <div key={axis.id} className="shrink-0" style={{ width: RPS_CELL_WIDTH }}>
                    <RpsRiskCell
                      axis={axis.id}
                      level={getRpsRisk(population.rpsRisks, axis.id)?.level ?? null}
                      label={`${axis.label}, ${population.name}`}
                    />
                  </div>
                ))}
              </div>
            ))}
        </Modal>
      </>
    )
  }

  return (
    <Modal
      title="Déplacer vers RPS"
      isLarge
      disableClickOutside
      isCloseButtonVisible
      onClose={() => !isMoving && onClose()}
      onNext={move}
      onNextCta="Continuer"
      nextDisabled={isMoving || !movableRisks.length}
      status={isMoving ? STATUS_SAVING : undefined}>
      {risks.map((risk) => {
        const axis = axes[risk.id] ?? RPS_RISK_AXES[0].id
        return (
          <ListItem key={risk.id}>
            <div className="min-w-0 flex-1 truncate">{risk.cName}</div>
            {risk.populationIds.length ? (
              <ListDropdown
                isCaretAlwaysVisible
                active={axis}
                value={AXES.find((item) => item.id === axis)?.name}
                values={AXES}
                onSelect={(item) => setAxes({ ...axes, [risk.id]: item.id })}
              />
            ) : (
              <span className="grey-t text-sm">Sans fonction : reste un risque physique</span>
            )}
          </ListItem>
        )
      })}
    </Modal>
  )
}
